import React from 'react';
import { Link } from 'react-router-dom';
import { ArrowUpRight } from 'lucide-react';
import { motion } from 'framer-motion';
import { useLanguage } from '../i18n/LanguageContext';
import { cases } from '../data/cases';
import { Reveal } from '../components/Reveal';
import { CTABand } from '../components/CTABand';

export default function Home() {
  const { lang, t } = useLanguage();
  const h = t.home;
  const units = t.units.items;
  const partners = h.partnersList;
  const featured = cases.slice(0, 3);

  return (
    <div data-testid="page-home">
      {/* Hero */}
      <section className="px-6 lg:px-12 pt-32 md:pt-40 pb-16">
        <div className="max-w-[1400px] mx-auto">
          <motion.p
            initial={{ opacity: 0, y: 12 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="overline mb-10"
          >
            {h.overline}
          </motion.p>

          <div className="grid md:grid-cols-12 gap-12 items-end">
            <div className="md:col-span-8">
              <motion.h1
                initial={{ opacity: 0, y: 24 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.05, ease: [0.22, 1, 0.36, 1] }}
                className="font-serif font-light text-[3.4rem] md:text-[6.2rem] leading-[0.98] tracking-tight"
                style={{ color: 'var(--ink)' }}
              >
                {h.heroTitle}
              </motion.h1>
            </div>
            <div className="md:col-span-4">
              <motion.p
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.2 }}
                className="text-[17px] leading-relaxed"
                style={{ color: 'var(--ink-2)' }}
              >
                {h.heroLead}
              </motion.p>
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                animate={{ opacity: 1, y: 0 }}
                transition={{ duration: 0.9, delay: 0.32 }}
                className="mt-8 flex flex-wrap items-center gap-6"
              >
                <Link to="/cases" data-testid="hero-cta-cases" className="link-underline text-[13px]">
                  {h.heroPrimaryCta}
                </Link>
                <Link to="/sobre" data-testid="hero-cta-about" className="text-[13px] opacity-60 hover:opacity-100 transition-opacity">
                  {h.heroSecondaryCta}
                </Link>
              </motion.div>
            </div>
          </div>

        </div>
      </section>

      {/* Intro narrative */}
      <section className="px-6 lg:px-12 py-24">
        <div className="max-w-[1400px] mx-auto grid md:grid-cols-12 gap-12">
          <div className="md:col-span-4">
            <Reveal>
              <p className="overline">{h.introOverline}</p>
            </Reveal>
          </div>
          <div className="md:col-span-8">
            <Reveal delay={0.05}>
              <h2
                className="font-serif text-3xl md:text-[2.7rem] font-light leading-[1.15] tracking-tight"
                style={{ color: 'var(--ink)' }}
              >
                {h.introTitle}
              </h2>
            </Reveal>
            <Reveal delay={0.12}>
              <p className="mt-8 text-[17px] leading-relaxed max-w-2xl" style={{ color: 'var(--ink-2)' }}>
                {h.introBody}
              </p>
            </Reveal>
            <Reveal delay={0.18}>
              <p className="mt-5 text-[17px] leading-relaxed max-w-2xl" style={{ color: 'var(--ink-2)' }}>
                {h.introBody2}
              </p>
            </Reveal>
            <Reveal delay={0.24}>
              <Link
                to="/sobre"
                data-testid="home-about-link"
                className="mt-10 inline-flex link-underline text-[13px]"
              >
                {h.introLink}
                <ArrowUpRight size={14} />
              </Link>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Studio units */}
      <section
        className="px-6 lg:px-12 py-24"
        style={{ background: 'var(--paper-2)', borderTop: '1px solid var(--rule)', borderBottom: '1px solid var(--rule)' }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-12 gap-12 mb-16">
            <div className="md:col-span-4">
              <Reveal>
                <p className="overline">{h.unitsOverline}</p>
              </Reveal>
            </div>
            <div className="md:col-span-8">
              <Reveal delay={0.05}>
                <h2
                  className="font-serif text-3xl md:text-[2.7rem] font-light leading-[1.15] tracking-tight"
                  style={{ color: 'var(--ink)' }}
                >
                  {h.unitsTitle}
                </h2>
              </Reveal>
            </div>
          </div>

          <div className="grid md:grid-cols-3 gap-10 md:gap-12">
            {units.map((u, i) => (
              <Reveal key={u.key} delay={i * 0.08}>
                <div
                  className="pt-8 pr-4"
                  style={{ borderTop: '1px solid var(--rule-strong)' }}
                  data-testid={`unit-${u.key}`}
                >
                  <p className="font-serif italic text-lg" style={{ color: 'var(--accent)' }}>
                    {u.headline}
                  </p>
                  <h3 className="mt-3 font-serif text-3xl leading-tight" style={{ color: 'var(--ink)' }}>
                    {u.label}
                  </h3>
                  <p className="mt-5 text-[15px] leading-relaxed" style={{ color: 'var(--ink-2)' }}>
                    {u.body}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>

          <Reveal delay={0.15}>
            <Link
              to="/servicos"
              data-testid="home-services-link"
              className="mt-14 inline-flex link-underline text-[13px]"
            >
              {t.nav.services}
              <ArrowUpRight size={14} />
            </Link>
          </Reveal>
        </div>
      </section>

      {/* Partners */}
      <section className="px-6 lg:px-12 py-24">
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-12 gap-12 mb-14">
            <div className="md:col-span-4">
              <Reveal>
                <p className="overline">{h.partnersOverline}</p>
              </Reveal>
            </div>
            <div className="md:col-span-8">
              <Reveal delay={0.05}>
                <h2
                  className="font-serif text-2xl md:text-3xl font-light leading-snug max-w-3xl"
                  style={{ color: 'var(--ink)' }}
                >
                  {h.partnersTitle}
                </h2>
              </Reveal>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-3" style={{ borderTop: '1px solid var(--rule)' }}>
            {partners.map((name, i) => (
              <Reveal key={name} delay={(i % 3) * 0.06}>
                <div
                  className="py-8 pr-6"
                  style={{
                    borderBottom: '1px solid var(--rule)',
                    borderRight: (i % 3) < 2 ? '1px solid var(--rule)' : 'none',
                  }}
                  data-testid={`partner-${i}`}
                >
                  <p className="font-serif text-xl md:text-2xl leading-tight" style={{ color: 'var(--ink)' }}>
                    {name}
                  </p>
                </div>
              </Reveal>
            ))}
          </div>
        </div>
      </section>

      {/* Cases preview */}
      <section
        className="px-6 lg:px-12 py-24"
        style={{ background: 'var(--paper-3)' }}
      >
        <div className="max-w-[1400px] mx-auto">
          <div className="grid md:grid-cols-12 gap-8 items-end mb-14">
            <div className="md:col-span-8">
              <Reveal>
                <p className="overline mb-6">{h.casesOverline}</p>
              </Reveal>
              <Reveal delay={0.05}>
                <h2 className="font-serif text-3xl md:text-[2.7rem] font-light leading-tight tracking-tight" style={{ color: 'var(--ink)' }}>
                  {h.casesTitle}
                </h2>
              </Reveal>
            </div>
            <div className="md:col-span-4 md:text-right">
              <Reveal delay={0.1}>
                <Link
                  to="/cases"
                  data-testid="home-cases-link"
                  className="inline-flex link-underline text-[13px]"
                >
                  {h.casesLink}
                  <ArrowUpRight size={14} />
                </Link>
              </Reveal>
            </div>
          </div>

          <div
            className="grid md:grid-cols-3"
            style={{ borderTop: '1px solid var(--rule)' }}
          >
            {featured.map((c, i) => {
              const isExternal = Boolean(c.url && c.url !== '#');
              const Wrapper = isExternal ? 'a' : 'div';

              const wrapperProps = isExternal
                ? {
                  href: c.url,
                  target: '_blank',
                  rel: 'noopener noreferrer',
                }
                : {};

              return (
                <Reveal key={`${c.id}-${c.year}`} delay={i * 0.08}>
                  <Wrapper
                    {...wrapperProps}
                    className="group block h-full py-9 md:px-8 first:pl-0"
                    style={{
                      borderBottom: '1px solid var(--rule)',
                      borderRight: i < featured.length - 1
                        ? '1px solid var(--rule)'
                        : 'none',
                    }}
                    data-testid={`home-case-${c.id}`}
                  >
                    <div className="flex items-start justify-between gap-4">
                      <span
                        className="text-[12px] tracking-[0.16em]"
                        style={{ color: 'var(--ink-3)' }}
                      >
                        {String(i + 1).padStart(2, '0')}
                      </span>

                      {isExternal && (
                        <ArrowUpRight
                          size={18}
                          className="shrink-0 transition-transform duration-300 group-hover:translate-x-1 group-hover:-translate-y-1"
                          style={{ color: 'var(--ink)' }}
                        />
                      )}
                    </div>

                    <p className="overline mt-10">
                      {c.service[lang]} · {c.year}
                    </p>

                    <h3
                      className="mt-4 font-serif text-2xl md:text-3xl font-light leading-snug"
                      style={{ color: 'var(--ink)' }}
                    >
                      {c.name}
                    </h3>

                    <p
                      className="mt-5 text-[15px] leading-relaxed"
                      style={{ color: 'var(--ink-2)' }}
                    >
                      {c.description[lang]}
                    </p>

                    <span
                      className="mt-7 inline-flex items-center gap-2 text-[12px] tracking-wide italic"
                      style={{ color: 'var(--ink-3)' }}
                    >
                      {isExternal ? t.portfolio.visit : t.portfolio.internal}
                    </span>
                  </Wrapper>
                </Reveal>
              );
            })}
          </div>
        </div>
      </section>

      <CTABand
        title={h.closingTitle}
        body={h.closingBody}
        email={h.closingEmail}
        testid="home-cta"
      />
    </div>
  );
}
